import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
} from "react";
import axios from "axios";
import { getToken } from "../utils/tokenUtils";
import { useCampaigns } from "./CampaignsContext";

// Create the CandidateContext
const CandidateContext = createContext();

// Custom hook to use the CandidateContext
export const useCandidates = () => useContext(CandidateContext);

// Provider component
export const CandidateProvider = ({ children }) => {
  const [candidates, setCandidates] = useState([]);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    perPage: 10,
  });
  const [errors, setErrors] = useState({});
  const API_URL = "https://hrmsnew.prepstripe.com/api/candidates"; // Replace with your actual API URL
  const token = getToken(); // Retrieve the token
  const [loading, setLoading] = useState(false);

  const { campaigns } = useCampaigns();

  // Fetch candidates from the API on load
  const fetchCandidates = async (page = 1) => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}?page=${page}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      await new Promise((r) => setTimeout(r, 1000));
      setLoading(false);
      // setCandidates(response.data?.data?.data || []);
      // setCandidates(response.data?.data?.data || []);
      setCandidates(response.data?.data?.data?.slice().reverse());

      setPagination({
        currentPage: response.data?.data?.current_page || 1,
        totalPages: response.data?.data?.last_page || 1,
        totalItems: response.data?.data?.total || 0,
        perPage: response.data?.data?.per_page || 10,
      });
    } catch (error) {
      console.error("Error fetching candidates:", error);
    } finally {
      setLoading(false);
    }
  };

  // Use the fetch function in useEffect
  useEffect(() => {
    fetchCandidates();
  }, [API_URL, token]);

  const addCandidate = async (newCandidate) => {
    try {
      setLoading(true);
      const response = await axios.post(API_URL, newCandidate, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const newCandidateWithCampaign = response.data?.data; // Ensure the campaign details are included
      setCandidates((prev) => [newCandidateWithCampaign, ...prev]);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.error("Error adding candidate:", error);
      throw error.response?.data; // Pass errors back to the component
    }
  };

  // Update an existing candidate
  const updateCandidate = async (updatedCandidate) => {
    try {
      // console.log("updatedCandidate", updatedCandidate)
      setLoading(true);

      // Only send allowed fields
      const allowedFields = [
        "id",
        "campaign_id",
        "contact_no",
        "created_at",
        "date_of_birth",
        "email",
        "experience",
      ];
      const cleanedCandidate = {};
      for (const key of allowedFields) {
        if (updatedCandidate[key] !== undefined) {
          cleanedCandidate[key] = updatedCandidate[key];
        }
      }

      const isFormData = cleanedCandidate instanceof FormData;
      const headers = {
        Authorization: `Bearer ${token}`,
        ...(isFormData ? { "Content-Type": "multipart/form-data" } : {}),
      };

      // console.log(`${API_URL}/${cleanedCandidate.id}`)
      // console.log("about to send the payload", cleanedCandidate)
      const response = await axios.post(
        `${API_URL}/${cleanedCandidate.id}`,
        cleanedCandidate,
        {
          headers,
        },
      );

      // console.log(response.data)

      setCandidates((prev) =>
        prev.map((candidate) =>
          candidate.id === updatedCandidate.id
            ? {
                ...candidate,
                ...response.data?.data,
                campaign:
                  campaigns.find(
                    (c) =>
                      String(c.id) === String(response.data?.data?.campaign_id),
                  ) || candidate.campaign,
              }
            : candidate,
        ),
      );
      setLoading(false);

      setErrors({}); // Clear errors on successful update
      return response.data?.data; // Optionally return updated data
    } catch (error) {
      setLoading(false);
      console.error("Error updating candidate:", error);
      const generalError =
        error.response?.data?.message || "Something went wrong.";
      setErrors({
        general: generalError,
        ...(error.response?.data?.errors || {}),
      });
      throw error; // Optionally rethrow for higher-level handling
    }
  };

  // Update candidate status
  const updateCandidateStatus = async (updatedCandidateStatus) => {
    try {
      const isFormData = updatedCandidateStatus instanceof FormData;
      const headers = {
        Authorization: `Bearer ${token}`,
        ...(isFormData ? { "Content-Type": "multipart/form-data" } : {}),
      };

      // console.log(`${API_URL}/candidates/status`)
      const response = await axios.post(
        `${API_URL}/status`,
        updatedCandidateStatus,
        {
          headers,
        },
      );

      // console.log(response.data)

      // old code
      // setCandidates((prev) =>
      //   prev.map((candidate) =>
      //     candidate.id === updatedCandidate.id ? { ...candidate, ...response.data?.data } : candidate
      //   )
      // );

      // new code
      // in previous code, we were updating only candidate's data but not campaign data.
      // i have imported Campaign context to update campaign data in the table.
      setCandidates((prev) =>
        prev.map((candidate) =>
          candidate.id === updatedCandidateStatus.id
            ? {
                ...candidate,
                ...response.data?.data,
                campaign:
                  campaigns.find(
                    (c) =>
                      String(c.id) === String(response.data?.data?.campaign_id),
                  ) || candidate.campaign,
              }
            : candidate,
        ),
      );

      setErrors({}); // Clear errors on successful update
      return response.data?.data; // Optionally return updated data
    } catch (error) {
      console.error("Error updating candidate status:", error);
      const generalError =
        error.response?.data?.message || "Something went wrong.";
      setErrors({
        general: generalError,
        ...(error.response?.data?.errors || {}),
      });
      throw error; // Optionally rethrow for higher-level handling
    }
  };

  // Delete a candidate
  const deleteCandidate = async (id) => {
    try {
      await axios.delete(`${API_URL}/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setCandidates((prev) => prev.filter((candidate) => candidate.id !== id));
    } catch (error) {
      console.error("Error deleting candidate:", error);
    }
  };

  // Memoize the context value
  const value = useMemo(
    () => ({
      loading,
      candidates,
      pagination,
      fetchCandidates,
      addCandidate,
      updateCandidate,
      updateCandidateStatus,
      deleteCandidate,
      errors,
    }),
    [candidates, errors, loading], // Recalculate the value only when these dependencies change
  );

  return (
    <CandidateContext.Provider value={value}>
      {children}
    </CandidateContext.Provider>
  );
};
